/**
 * CodeMirror extension that flashes the lines an instant text apply rewrote.
 * The editor pane dispatches `setInstantApplyHighlight` with the applied line
 * range after the saved source reaches the document; the field decorates
 * those lines and a CSS animation fades the tint out. Dispatching `null`
 * clears the decorations (done after the animation, so a line scrolled out
 * of the viewport and back doesn't replay the flash — CodeMirror re-creates
 * line DOM on viewport changes).
 */
import {
  StateEffect,
  StateField,
  type Extension,
  type Range,
} from '@codemirror/state';
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view';
import type {VCCInstantApplyLineRange} from '../flux/actionCreators';

export const setInstantApplyHighlight =
  StateEffect.define<VCCInstantApplyLineRange | null>();

/** Keep in sync with the animation duration in the theme below. */
export const INSTANT_APPLY_HIGHLIGHT_DURATION_MS = 2000;

const lineDecoration = Decoration.line({class: 'cm-vccInstantApplyLine'});

const instantApplyHighlightField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(decorations, transaction) {
    let next = decorations.map(transaction.changes);
    for (const effect of transaction.effects) {
      if (!effect.is(setInstantApplyHighlight)) continue;
      if (effect.value === null) {
        next = Decoration.none;
        continue;
      }
      const doc = transaction.state.doc;
      // Clamp defensively: the range was computed against the saved source,
      // which the document should already hold — but a shorter doc must not
      // make doc.line() throw.
      const fromLine = Math.max(1, Math.min(effect.value.fromLine, doc.lines));
      const toLine = Math.max(
        fromLine,
        Math.min(effect.value.toLine, doc.lines)
      );
      const lines: Range<Decoration>[] = [];
      for (let line = fromLine; line <= toLine; line++) {
        lines.push(lineDecoration.range(doc.line(line).from));
      }
      next = Decoration.set(lines);
    }
    return next;
  },
  provide: (field) => EditorView.decorations.from(field),
});

const instantApplyHighlightTheme = EditorView.baseTheme({
  // A translucent tint reads on both the light and dark Spring editor themes.
  '.cm-vccInstantApplyLine': {
    animation: `cm-vccInstantApplyFlash ${INSTANT_APPLY_HIGHLIGHT_DURATION_MS}ms ease-out forwards`,
  },
  '@keyframes cm-vccInstantApplyFlash': {
    from: {backgroundColor: 'rgba(255, 200, 50, 0.22)'},
    '80%': {backgroundColor: 'rgba(255, 200, 50, 0.22)'},
    to: {backgroundColor: 'transparent'},
  },
});

export const instantApplyHighlightExtension: Extension = [
  instantApplyHighlightField,
  instantApplyHighlightTheme,
];
